import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type CardProps = HTMLAttributes<HTMLDivElement>;

/**
 * The surfaces a `Card` can be — slice 65, from the concept's `.card--brand` and
 * `.card--link`.
 *
 *   - `default` — the hairline over a white wash every panel has always had.
 *     The string is **exactly** the one this component rendered before the
 *     prop existed: the admin app uses the same Card, and suites read the
 *     class string, so no caller that passes nothing may see a byte change.
 *   - `brand` — a violet hairline over a violet wash, for the one card on a
 *     screen that is about Wend itself rather than the workspace's data.
 *   - `interactive` — the default surface that answers the pointer: the border
 *     warms to `brand-b` and the card lifts 2px. The lift is movement, so it is
 *     switched off under `prefers-reduced-motion`; the border change is not
 *     movement and stays. `has-[:focus-visible]` gives a keyboard user the same
 *     border a mouse gets, because the focusable thing is the link *inside* the
 *     card and the card itself never receives focus.
 *
 * A record rather than a `cva` because there is one axis and no compounds, and
 * because `default` has to be a literal a reader can compare against git
 * history.
 *
 * Exported (slice 66) for `SourceCard`, which has to wear the `interactive`
 * skin on an `<a>` or a `<button>` rather than a `<div>`. Reading the string
 * from here, rather than copying it, is what keeps the two from drifting.
 */
export const CARD_SURFACE = {
  default: 'rounded-lg border border-line bg-linear-to-b from-white/[0.045] to-white/[0.015]',
  brand: 'rounded-lg border border-brand-b/35 bg-linear-to-b from-brand-a/14 to-brand-b/[0.04]',
  interactive: cn(
    'rounded-lg border border-line bg-linear-to-b from-white/[0.045] to-white/[0.015]',
    // `translate`, not `transform`: Tailwind v4's `-translate-y-*` sets the
    // separate CSS `translate` property, so transitioning `transform` left the
    // lift snapping into place while only the border eased.
    'transition-[border-color,translate] duration-200',
    'hover:-translate-y-0.5 hover:border-brand-b/50 has-[:focus-visible]:border-brand-b/50',
    'motion-reduce:hover:translate-y-0',
  ),
} as const;

export type CardVariant = keyof typeof CARD_SURFACE;

export interface CardRootProps extends CardProps {
  /** Default `'default'` — see {@link CARD_SURFACE}. */
  variant?: CardVariant;
}

/**
 * The surface every panel sits on: a hairline border over a barely-there
 * white wash, at the `lg` radius. Padding lives on the sections, not the card,
 * so a card can hold a flush list or table as easily as prose.
 *
 * `variant` is additive (slice 65); `CardProps` stays the plain div props the
 * header, content and footer share, so none of them grows a prop that means
 * nothing on a section.
 */
export const Card = forwardRef<HTMLDivElement, CardRootProps>(function Card(
  { className, variant = 'default', ...rest },
  ref,
) {
  return <div ref={ref} className={cn(CARD_SURFACE[variant], className)} {...rest} />;
});

export const CardHeader = forwardRef<HTMLDivElement, CardProps>(function CardHeader(
  { className, ...rest },
  ref,
) {
  return <div ref={ref} className={cn('flex flex-col gap-1.5 p-6 pb-4', className)} {...rest} />;
});

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /**
   * A few words of provenance, set small and mono at the far end of the title
   * row — "from your data", "last 30 days", "4 open".
   *
   * It answers *where did this come from* without spending a sentence on it,
   * which is the whole reason it exists: the alternative every card reaches for
   * is a `CardDescription` underneath, and nine of those is how this page got to
   * 1,250 words.
   *
   * **Additive on purpose.** Almost every call site passes no `sub` — at the
   * time of writing, two of them do — and for the rest this renders exactly the
   * `<h2>` it always did, because the flex row is only added when there is a
   * second thing to lay out.
   *
   * The sentence here used to say "Forty-six call sites", which was wrong by
   * more than it was right and had been recopied onto two other files by the
   * time anybody counted. A census in a comment is a fact with an expiry date
   * and no test; the shape of the claim is what matters, so that is what it
   * says now.
   */
  sub?: ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(
  { className, sub, children, ...rest },
  ref,
) {
  return (
    <h2
      ref={ref}
      className={cn(
        'text-[1.15rem] font-semibold leading-tight tracking-[-0.022em]',
        sub !== undefined && 'flex items-baseline justify-between gap-3',
        className,
      )}
      {...rest}
    >
      {children}
      {sub !== undefined && (
        <span className="shrink-0 font-mono text-[0.66rem] font-normal uppercase tracking-[0.11em] text-muted">
          {sub}
        </span>
      )}
    </h2>
  );
});

export type CardDescriptionProps = HTMLAttributes<HTMLParagraphElement>;

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  function CardDescription({ className, ...rest }, ref) {
    return (
      <p ref={ref} className={cn('text-[0.9rem] leading-relaxed text-muted', className)} {...rest} />
    );
  },
);

export const CardContent = forwardRef<HTMLDivElement, CardProps>(function CardContent(
  { className, ...rest },
  ref,
) {
  return <div ref={ref} className={cn('p-6 pt-0', className)} {...rest} />;
});

export const CardFooter = forwardRef<HTMLDivElement, CardProps>(function CardFooter(
  { className, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('flex items-center gap-3 p-6 pt-0', className)} {...rest} />
  );
});
